/**
 * 근태 시드 — 휴가(vacation_request), 일정 공유(user_schedule), 휴일(holiday), 연차 부여
 * (vacation_grant). Notion 스키마 모양 그대로다.
 *
 * 휴가는 결재를 받는 신청이고, 일정 공유(외근·출장·기타)는 결재 없이 넣으면 바로 확정이다. 휴가 유형은
 * 백엔드처럼 연차·기타 둘이고, 반차·반반차는 그 하루의 시각 범위로 남는다(09시~13시처럼). 휴일은
 * 하루에 한 행이므로 연휴는 날짜마다 한 행씩 깔린다.
 */
import type {
  ScheduleCategory,
  UserSchedule,
  Vacation,
  VacationGrant,
  VacationType,
} from "@/types";
import { holidayDates, nextDay } from "./holidays";
import { at } from "./seed";
import { staff } from "./staff";

const STANDARD_HOURS = 8;

const nameOf = (userId: string) => staff.find((u) => u.id === userId)?.name ?? userId;

/* ── 휴가 유형 ─────────────────────────────────────────────────────────── */

/**
 * 시드의 휴가 이름 → 백엔드 휴가 유형(연차·기타). 반차·반반차는 유형이 아니라 연차를 하루 안의 시각
 * 범위로 쓴 것이고, 예비군·경조사처럼 연차를 깎지 않는 휴가는 기타다.
 */
const TYPE_OF: Record<string, VacationType> = {
  "vk-annual": "annual",
  "vk-half": "annual",
  "vk-quarter": "annual",
  "vk-reserve": "other",
  "vk-family": "other",
  "vk-other": "other",
};

/** 백엔드 VACATION_TYPE_LABELS 와 같다. */
export const VACATION_TYPE_LABEL: Record<VacationType, string> = { annual: "연차", other: "기타" };

/* ── 휴가 ──────────────────────────────────────────────────────────────── */

type Decision =
  { kind: "auto"; on: string } | { kind: "approved"; by: string; on: string; note?: string } | null;

interface VacationSeed {
  id: string;
  user: string;
  /** 시드의 휴가 이름(vk-…) — TYPE_OF 로 백엔드 유형이 된다 */
  type: string;
  start: string;
  end?: string;
  /** 하루보다 짧게 쓸 때만. 30분 단위 */
  time?: [string, string];
  reason: string;
  requested_on: string;
  decision: Decision;
  /** 승인된 휴가의 취소 신청 — 관리자 결정을 기다린다 */
  cancel_requested?: string;
}

const auto = (on: string): Decision => ({ kind: "auto", on });
const by = (who: string, on: string, note?: string): Decision => ({
  kind: "approved",
  by: who,
  on,
  note,
});

const VACATION_SEED: VacationSeed[] = [
  {
    id: "01",
    user: "u-pm",
    type: "vk-annual",
    start: "2026-09-15",
    reason: "개인 연차",
    requested_on: "2026-09-04",
    decision: auto("2026-09-04"),
  },
  {
    id: "03",
    user: "u-pm",
    type: "vk-annual",
    start: "2026-11-02",
    end: "2026-11-03",
    reason: "가족 여행",
    requested_on: "2026-10-21",
    decision: null,
  },
  {
    id: "04",
    user: "u-pm2",
    type: "vk-annual",
    start: "2026-09-29",
    end: "2026-09-30",
    reason: "추석 연휴 연결",
    requested_on: "2026-09-10",
    decision: by("변경민", "2026-09-11"),
  },
  // 오후 반차 — 유형은 반차, 그 하루의 14시부터 18시까지다.
  {
    id: "06",
    user: "u-pm3",
    type: "vk-half",
    start: "2026-09-17",
    time: ["14:00", "18:00"],
    reason: "병원",
    requested_on: "2026-09-08",
    decision: auto("2026-09-08"),
  },
  {
    id: "07",
    user: "u-staff",
    type: "vk-half",
    start: "2026-09-08",
    time: ["09:00", "13:00"],
    reason: "병원",
    requested_on: "2026-08-31",
    decision: auto("2026-08-31"),
  },
  {
    id: "08",
    user: "u-staff",
    type: "vk-quarter",
    start: "2026-10-16",
    time: ["16:00", "18:00"],
    reason: "은행 업무",
    requested_on: "2026-10-07",
    decision: auto("2026-10-07"),
  },
  {
    id: "09",
    user: "u-staff",
    type: "vk-annual",
    start: "2026-10-29",
    end: "2026-10-30",
    reason: "가족 행사",
    requested_on: "2026-10-19",
    decision: null,
  },
  {
    id: "10",
    user: "u-st2",
    type: "vk-annual",
    start: "2026-09-21",
    end: "2026-09-23",
    reason: "추석 연휴 연결",
    requested_on: "2026-09-03",
    decision: by("u-owner", "2026-09-04"),
  },
  // 경조사 — 연차 잔여에서 빠지지 않는다. 사유가 반드시 있다.
  {
    id: "24",
    user: "u-st2",
    type: "vk-family",
    start: "2026-10-26",
    end: "2026-10-27",
    reason: "경조사",
    requested_on: "2026-10-20",
    decision: by("변경민", "2026-10-20"),
  },
  {
    id: "12",
    user: "u-st3",
    type: "vk-annual",
    start: "2026-09-11",
    reason: "이사",
    requested_on: "2026-09-01",
    decision: auto("2026-09-01"),
  },
  {
    id: "13",
    user: "u-st3",
    type: "vk-annual",
    start: "2026-10-23",
    reason: "개인 연차",
    requested_on: "2026-10-19",
    decision: null,
  },
  {
    id: "14",
    user: "u-st4",
    type: "vk-annual",
    start: "2026-09-18",
    reason: "개인 연차",
    requested_on: "2026-09-07",
    decision: null,
  },
  {
    id: "16",
    user: "u-st4",
    type: "vk-half",
    start: "2026-10-13",
    time: ["14:00", "18:00"],
    reason: "병원",
    requested_on: "2026-10-05",
    decision: auto("2026-10-05"),
  },
  {
    id: "17",
    user: "u-st5",
    type: "vk-annual",
    start: "2026-10-02",
    reason: "학교 행사",
    requested_on: "2026-09-22",
    decision: auto("2026-09-22"),
  },
  {
    id: "18",
    user: "u-st6",
    type: "vk-annual",
    start: "2026-09-14",
    reason: "개인 연차",
    requested_on: "2026-09-02",
    decision: auto("2026-09-02"),
  },
  {
    id: "19",
    user: "u-acc",
    type: "vk-half",
    start: "2026-09-04",
    time: ["09:00", "13:00"],
    reason: "병원",
    requested_on: "2026-08-26",
    decision: auto("2026-08-26"),
  },
  // 승인됐지만 취소 신청을 낸 건 — 관리자 결정을 기다린다.
  {
    id: "20",
    user: "u-acc",
    type: "vk-annual",
    start: "2026-10-08",
    reason: "개인 연차",
    requested_on: "2026-09-28",
    decision: auto("2026-09-28"),
    cancel_requested: "2026-10-05",
  },
  {
    id: "21",
    user: "변경민",
    type: "vk-annual",
    start: "2026-09-22",
    end: "2026-09-23",
    reason: "추석 연휴 연결",
    requested_on: "2026-09-07",
    decision: by("u-owner", "2026-09-07"),
  },
  // 예비군 — 연차를 깎지 않는 휴가 유형이다(예전에는 기타 일정으로 넣었다).
  {
    id: "22",
    user: "변경민",
    type: "vk-reserve",
    start: "2026-10-05",
    reason: "예비군 훈련",
    requested_on: "2026-09-25",
    decision: by("u-owner", "2026-09-25"),
  },
];

const isWeekendDay = (date: string) => [0, 6].includes(new Date(`${date}T00:00:00Z`).getUTCDay());
const isWorkday = (date: string) => !isWeekendDay(date) && !holidayDates.has(date);

/** 종일은 근무일 수, 부분 휴가는 시간 ÷ 하루 기준 시간. 점심은 빼지 않는다. */
function daysOf(seed: VacationSeed): number {
  let count = 0;
  for (let day = seed.start; day <= (seed.end ?? seed.start); day = nextDay(day))
    if (isWorkday(day)) count++;
  if (!seed.time) return count;
  if (!count) return 0;
  const [start, end] = seed.time;
  const minutes =
    Number(end.slice(0, 2)) * 60 +
    Number(end.slice(3)) -
    (Number(start.slice(0, 2)) * 60 + Number(start.slice(3)));
  return Math.round((minutes / 60 / STANDARD_HOURS) * 100) / 100;
}

export const vacations: Vacation[] = VACATION_SEED.map((seed) => {
  const decision = seed.decision;
  const approved = decision !== null;
  return {
    id: `vacation-${seed.id}`,
    user_id: seed.user,
    user_name: nameOf(seed.user),
    type: TYPE_OF[seed.type],
    type_label: VACATION_TYPE_LABEL[TYPE_OF[seed.type]],
    start_date: seed.start,
    end_date: seed.end ?? seed.start,
    start_time: seed.time ? `${seed.time[0]}:00` : null,
    end_time: seed.time ? `${seed.time[1]}:00` : null,
    days: daysOf(seed),
    reason: seed.reason,
    status: approved ? "approved" : "pending",
    auto_approved: decision?.kind === "auto",
    approver_id: decision?.kind === "approved" ? decision.by : null,
    decided_at: decision ? at(decision.on, "11:30") : null,
    decision_note: decision?.kind === "approved" ? (decision.note ?? "") : "",
    cancel_requested_at: seed.cancel_requested ? at(seed.cancel_requested, "09:40") : null,
    created_at: at(seed.requested_on, "10:12"),
  };
});

/* ── 일정 공유 ─────────────────────────────────────────────────────────── */

interface ScheduleSeed {
  id: string;
  user: string;
  kind: "field" | "trip" | "other";
  project?: string;
  start: string;
  end?: string;
  times?: [string, string];
  reason: string;
  created_on: string;
}

const SCHEDULE_SEED: ScheduleSeed[] = [
  {
    id: "02",
    user: "u-pm",
    kind: "field",
    project: "p-2603",
    start: "2026-10-06",
    times: ["13:00", "18:00"],
    reason: "망원 수변 산책로 현장 답사",
    created_on: "2026-10-02",
  },
  {
    id: "05",
    user: "u-pm2",
    kind: "field",
    project: "p-2604",
    start: "2026-10-14",
    times: ["10:00", "15:00"],
    reason: "누리홀딩스 기본설계 보고",
    created_on: "2026-10-13",
  },
  {
    id: "11",
    user: "u-st2",
    kind: "trip",
    project: "p-2512",
    start: "2026-10-07",
    end: "2026-10-08",
    reason: "덕은 공동주택 고양시 협의 출장",
    created_on: "2026-10-05",
  },
  {
    id: "15",
    user: "u-st4",
    kind: "field",
    project: "p-2607",
    start: "2026-10-01",
    times: ["09:30", "13:00"],
    reason: "이천 물류센터 자재 확인",
    created_on: "2026-09-29",
  },
  {
    id: "25",
    user: "u-st3",
    kind: "field",
    project: "p-2603",
    start: "2026-10-29",
    times: ["13:00", "17:00"],
    reason: "망원 수변 산책로 시공 상태 확인",
    created_on: "2026-10-27",
  },
  {
    id: "26",
    user: "u-pm",
    kind: "trip",
    project: "p-2608",
    start: "2026-11-04",
    end: "2026-11-05",
    reason: "강릉 솔바람 카페 인허가 협의",
    created_on: "2026-10-28",
  },
  {
    id: "23",
    user: "u-owner",
    kind: "trip",
    project: "p-2608",
    start: "2026-10-19",
    end: "2026-10-20",
    reason: "강릉 솔바람 카페 대지 답사",
    created_on: "2026-10-15",
  },
];

/**
 * 백엔드 마이그레이션이 깔아 두는 셋과 같은 id·이름·색이다. sort_order 는 휴가 유형과 이어진 한 순서다 —
 * 신청 폼에서 연차·반차·반반차 다음에 외근·출장이 온다.
 */
export const scheduleCategories: ScheduleCategory[] = [
  { id: "3f1a0001-0000-4000-8000-000000000001", name: "외근", color: "#18a5a5", sort_order: 40 },
  { id: "3f1a0001-0000-4000-8000-000000000002", name: "출장", color: "#03b26c", sort_order: 50 },
  {
    id: "3f1a0001-0000-4000-8000-000000000003",
    name: "기타 일정",
    color: "#fe9800",
    sort_order: 90,
  },
];

const CATEGORY_OF = { field: 0, trip: 1, other: 2 } as const;

export const schedules: UserSchedule[] = SCHEDULE_SEED.map((seed) => ({
  id: `schedule-${seed.id}`,
  user_id: seed.user,
  user_name: nameOf(seed.user),
  category_id: scheduleCategories[CATEGORY_OF[seed.kind]].id,
  category_name: scheduleCategories[CATEGORY_OF[seed.kind]].name,
  category_color: scheduleCategories[CATEGORY_OF[seed.kind]].color,
  starts_at: at(seed.start, seed.times?.[0] ?? "09:00"),
  ends_at: at(seed.end ?? seed.start, seed.times?.[1] ?? "18:00"),
  project_id: seed.project ?? null,
  reason: seed.reason,
  cancelled_at: null,
  created_at: at(seed.created_on, "10:12"),
}));

/* ── 연차 부여 ─────────────────────────────────────────────────────────── */

/** 올해 부여한 연차와, 시드 창(9월 1일) 전에 이미 쓴 연차(조정으로 깐다). */
const GRANT_SEED: Record<string, { granted: number; used_before: number }> = {
  "u-owner": { granted: 25, used_before: 6 },
  변경민: { granted: 17, used_before: 4 },
  "u-pm": { granted: 20, used_before: 8 },
  "u-pm2": { granted: 19, used_before: 5 },
  "u-pm3": { granted: 18, used_before: 2.5 },
  "u-staff": { granted: 15, used_before: 3 },
  "u-st2": { granted: 16, used_before: 3.5 },
  "u-st3": { granted: 11, used_before: 3 },
  "u-st4": { granted: 15, used_before: 9 },
  "u-st5": { granted: 3, used_before: 0 },
  "u-st6": { granted: 15, used_before: 12 },
  "u-acc": { granted: 17, used_before: 6 },
};

export const GRANT_PERIOD = { from: "2026-01-01", to: "2027-01-01" };

/** 부여 기간 한 건씩. 사용과 잔여는 handler 가 휴가에서 센다 — 컬럼으로 두지 않는다. */
export const vacationGrants: Omit<VacationGrant, "used" | "remaining">[] = Object.entries(
  GRANT_SEED
).map(([userId, seed]) => ({
  id: `grant-${userId}`,
  user_id: userId,
  user_name: nameOf(userId),
  period_from: GRANT_PERIOD.from,
  period_to: GRANT_PERIOD.to,
  granted: seed.granted,
  adjusted: -seed.used_before,
  note: seed.used_before ? "2026-09 이전 사용분" : "",
}));

/**
 * 지난해 몫 — 연차 내역 표에 만료된 줄이 보이게. 다 쓰고 남은 날이
 * 소멸해 잔여가 0 이다(조정으로 맞춘다). 잔여를 남기면 지금 남은 연차(잔여의 합)에 섞여 들어간다.
 */
vacationGrants.push(
  ...Object.entries(GRANT_SEED).map(([userId, seed]) => ({
    id: `grant-${userId}-2025`,
    user_id: userId,
    user_name: nameOf(userId),
    period_from: "2025-01-01",
    period_to: "2026-01-01",
    granted: seed.granted - 1,
    adjusted: -(seed.granted - 1),
    note: "2025년 사용분 · 남은 날 소멸",
  }))
);

/** 승인된 휴가로 그날 일하지 않은 시간 — Daily 시드가 그날의 한 일 시간을 줄이는 데 쓴다. */
export function leaveHoursOn(userId: string, date: string): number {
  const vacation = vacations.find(
    (row) =>
      row.user_id === userId &&
      row.status === "approved" &&
      row.start_date <= date &&
      date <= row.end_date
  );
  if (!vacation) return 0;
  if (!vacation.start_time || !vacation.end_time) return STANDARD_HOURS;
  return (
    (Number(vacation.end_time.slice(0, 2)) * 60 +
      Number(vacation.end_time.slice(3, 5)) -
      (Number(vacation.start_time.slice(0, 2)) * 60 + Number(vacation.start_time.slice(3, 5)))) /
    60
  );
}
